/*!
 * CDP Recommendations frontend integration.
 *
 * Reads <meta name="cdp-recommendation" content="group:handle1,handle2"> declarations
 * from <head>, polls localStorage for `cdp::session_id` (set by the web pixel), fetches
 * a ranked ordering of category handles from the CDP Recommendation API, and applies
 * `data-cdp-show` / `data-cdp-if` directives on matching elements throughout the document.
 *
 * Adds `cdp-ready` to <html> after the first run so fallback CSS rules can defer to the
 * runtime state. Until then (and forever, if the session never resolves), the fallback CSS
 * emitted by `snippets/cdp-recommendation-meta.liquid` keeps only the first N declared
 * handles per group visible.
 */

(function cdpRecommendations() {
  "use strict";

  // ── Singleton guard ──

  if (window.__cdpRecommendationsLoaded) return;
  window.__cdpRecommendationsLoaded = true;

  // ── Config ──

  const CDP_API = "https://kosmos-cdp-backend.tim-maier.workers.dev/v1/recommend/categories";
  const SESSION_KEY = "cdp::session_id";
  const POLL_INTERVAL = 200;
  const POLL_TIMEOUT = 5000;
  const FETCH_TIMEOUT = 8000;
  const READY_CLASS = "cdp-ready";

  // ── State ──

  let observer = null;
  let inFlightController = null;
  let rerunScheduled = false;
  let sessionId = null;
  // Preserve original inline `display` for elements we toggle, so we can restore it.
  const originalDisplay = new WeakMap();

  // ── Parsing ──

  function parseAttr(value) {
    if (typeof value !== "string") return null;
    const idx = value.indexOf(":");
    if (idx === -1) return null;
    const group = value.slice(0, idx).trim();
    if (!group) return null;
    const handles = value
      .slice(idx + 1)
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean);
    return handles.length ? { group, handles } : null;
  }

  function parseTop(el) {
    const raw =
      (el.dataset && el.dataset.cdpTop) || el.getAttribute("data-cdp-top");
    if (!raw) return 1;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  // ── Scan ──

  function scanGroups(root) {
    const groups = new Map(); // group → { handles: Set, declared: [], top: number, present: Set }

    function record(name, handles, top) {
      let g = groups.get(name);
      if (!g) {
        g = { handles: new Set(), declared: [], top: 1, present: new Set() };
        groups.set(name, g);
      }
      for (const h of handles) {
        if (!g.handles.has(h)) {
          g.handles.add(h);
          g.declared.push(h);
        }
      }
      if (top > g.top) g.top = top;
    }

    const declarations = root.querySelectorAll(
      "[data-cdp-recommendation], " +
        'meta[name="cdp-recommendation"], ' +
        "template[data-cdp-recommendation]"
    );

    for (const el of declarations) {
      const raw =
        el.tagName === "META"
          ? el.getAttribute("content")
          : (el.dataset && el.dataset.cdpRecommendation) ||
            el.getAttribute("data-cdp-recommendation");
      const parsed = parseAttr(raw);
      if (!parsed) continue;
      record(parsed.group, parsed.handles, parseTop(el));
    }

    // Track which handles per group are actually rendered on this page (via
    // data-cdp-if / data-cdp-show). A group's metaobject typically declares
    // every possible category across the shop, while any given page renders
    // only a subset. Without this we may pick a winner that has no element
    // on this page, hiding everything CDP-controlled.
    const directiveEls = root.querySelectorAll(
      "[data-cdp-if], [data-cdp-show]"
    );
    for (const el of directiveEls) {
      const raw =
        (el.dataset && (el.dataset.cdpIf || el.dataset.cdpShow)) ||
        el.getAttribute("data-cdp-if") ||
        el.getAttribute("data-cdp-show");
      const parsed = parseAttr(raw);
      if (!parsed) continue;
      const g = groups.get(parsed.group);
      if (!g) continue;
      for (const h of parsed.handles) g.present.add(h);
    }

    const allHandles = new Set();
    for (const g of groups.values()) {
      for (const h of g.handles) allHandles.add(h);
    }

    return { groups, allHandles };
  }

  // ── Resolve ──

  function resolveWinners(groups, rankedHandles) {
    const winners = new Map();
    for (const [name, g] of groups) {
      // Restrict eligibility to handles actually rendered on this page when
      // we have that information; otherwise we risk picking a winner that
      // has no corresponding element here, which would hide everything.
      const eligible = g.present.size > 0 ? g.present : g.handles;
      const ranked = rankedHandles.filter((h) => eligible.has(h));
      const declared = g.declared.filter((h) => eligible.has(h));
      const source = ranked.length > 0 ? ranked : declared;
      winners.set(name, new Set(source.slice(0, g.top)));
    }
    return winners;
  }

  // ── Apply ──

  function isWinnerEl(parsed, winners) {
    const set = winners.get(parsed.group);
    if (!set) return false;
    for (const h of parsed.handles) {
      if (set.has(h)) return true;
    }
    return false;
  }

  function showElement(el) {
    if (originalDisplay.has(el)) {
      el.style.display = originalDisplay.get(el);
    } else {
      el.style.removeProperty("display");
    }
  }

  function hideElement(el) {
    if (!originalDisplay.has(el)) {
      // Capture the inline value (may be empty string) so we can restore it.
      originalDisplay.set(el, el.style.display);
    }
    el.style.display = "none";
  }

  function applyDirectives(root, winners) {
    const showEls = collectMatching(root, "[data-cdp-show]");
    for (const el of showEls) {
      const parsed = parseAttr(
        (el.dataset && el.dataset.cdpShow) || el.getAttribute("data-cdp-show")
      );
      if (!parsed) continue;
      if (isWinnerEl(parsed, winners)) showElement(el);
      else hideElement(el);
    }

    const ifEls = collectMatching(root, "[data-cdp-if]");
    for (const el of ifEls) {
      const parsed = parseAttr(
        (el.dataset && el.dataset.cdpIf) || el.getAttribute("data-cdp-if")
      );
      if (!parsed) continue;
      if (!isWinnerEl(parsed, winners)) {
        originalDisplay.delete(el);
        el.remove();
      }
    }
  }

  function collectMatching(root, selector) {
    if (root.nodeType !== 1) return [];
    const results = [];
    if (root.matches && root.matches(selector)) results.push(root);
    if (root.querySelectorAll) {
      for (const el of root.querySelectorAll(selector)) results.push(el);
    }
    return results;
  }

  // ── Fetch ──

  async function fetchRanked(handles) {
    if (inFlightController) inFlightController.abort();
    inFlightController = new AbortController();
    const ctrl = inFlightController;
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT);

    try {
      const url =
        CDP_API +
        "?session_id=" +
        encodeURIComponent(sessionId) +
        "&categories=" +
        encodeURIComponent([...handles].join(","));
      const res = await fetch(url, { signal: ctrl.signal, credentials: "omit" });
      if (!res.ok) return [];
      const data = await res.json();
      if (!Array.isArray(data)) return [];
      return data.filter((h) => typeof h === "string");
    } catch {
      return [];
    } finally {
      clearTimeout(timer);
      if (inFlightController === ctrl) inFlightController = null;
    }
  }

  // ── Run ──

  function markReady() {
    if (document.documentElement) {
      document.documentElement.classList.add(READY_CLASS);
    }
  }

  // Persist the per-group ranked handle list as cart attributes so the next
  // server-rendered pageview can pre-emit a sharper fallback CSS (winners only).
  // Underscore-prefixed keys are hidden from the merchant's order/admin views.
  // Best-effort: failures are swallowed; the runtime path still works without it.
  function persistRankedToCart(groups, rankedAll) {
    try {
      const params = new URLSearchParams();
      let any = false;
      for (const [name, g] of groups) {
        const groupRanked = rankedAll.filter((h) => g.handles.has(h));
        if (groupRanked.length === 0) continue;
        params.set(`attributes[_cdp_g_${name}]`, groupRanked.join(","));
        any = true;
      }
      if (!any) return;
      fetch("/cart/update.js", {
        method: "POST",
        body: params,
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }

  async function run() {
    const { groups, allHandles } = scanGroups(document);
    if (allHandles.size === 0) {
      markReady();
      ensureObserver();
      return;
    }

    const ranked = sessionId ? await fetchRanked(allHandles) : [];
    const winners = resolveWinners(groups, ranked);
    applyDirectives(document.body, winners);
    if (ranked.length > 0) persistRankedToCart(groups, ranked);
    markReady();
    ensureObserver();
  }

  function scheduleRerun() {
    if (rerunScheduled) return;
    rerunScheduled = true;
    // Coalesce bursts of mutations into a single re-run.
    requestAnimationFrame(() => {
      rerunScheduled = false;
      run();
    });
  }

  // ── Observe ──

  function ensureObserver() {
    if (observer || !document.body) return;
    observer = new MutationObserver((mutations) => {
      let needsRerun = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;

          // New declaration appeared → full re-scan + re-fetch.
          if (
            (node.matches &&
              node.matches(
                "[data-cdp-recommendation], template[data-cdp-recommendation]"
              )) ||
            (node.querySelector &&
              node.querySelector(
                "[data-cdp-recommendation], template[data-cdp-recommendation]"
              ))
          ) {
            needsRerun = true;
            break;
          }
        }
        if (needsRerun) break;
      }

      if (needsRerun) {
        scheduleRerun();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function cleanup() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (inFlightController) {
      inFlightController.abort();
      inFlightController = null;
    }
  }

  window.addEventListener("pagehide", cleanup, { once: true });

  // ── Boot ──

  function boot() {
    let elapsed = 0;
    const poll = setInterval(() => {
      const id = (() => {
        try {
          return localStorage.getItem(SESSION_KEY);
        } catch {
          return null;
        }
      })();

      if (id) {
        clearInterval(poll);
        sessionId = id;
        run();
        return;
      }

      elapsed += POLL_INTERVAL;
      if (elapsed >= POLL_TIMEOUT) {
        clearInterval(poll);
        // No session resolved → apply declared-order fallback so content isn't stuck
        // hidden behind the fallback CSS.
        run();
      }
    }, POLL_INTERVAL);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
